/* eslint-disable no-underscore-dangle */
import React, { useState, useCallback, useRef } from 'react';
import { useWillUnmount } from 'rooks';
import { AquaHowl, parseHowlConfig } from '../AquaHowl';
import useVoiceCaptions from './useVoiceCaptions';

const useAudioPlayer = () => {
  const howlRef = useRef();

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioVolume = useRef(1);
  const { startVoiceCaption, resetVoiceCaption } = useVoiceCaptions();

  const unloadAudio = useCallback(() => {
    if (howlRef.current && howlRef.current._state !== 'unloaded') {
      howlRef.current.unload();
    }
    resetVoiceCaption();
  }, [howlRef, resetVoiceCaption]);

  const pauseAudio = useCallback(() => {
    if (howlRef.current) {
      howlRef.current.pause();
      setIsAudioPlaying(false);
    }
  }, [howlRef]);

  const setVolume = useCallback((amount) => {
    if (howlRef.current) {
      howlRef.current.volume(amount);
    }
    audioVolume.current = amount;
  }, [howlRef]);

  const resumeAudio = useCallback(() => {
    if (howlRef.current) {
      howlRef.current.play();
      if (howlRef.current.playing()) {
        setIsAudioPlaying(true);
      }
    }
  }, [howlRef]);

  const playAudio = useCallback((...args) => {
    const {
      interrupt = false,
      caption = '',
      continuePlayingAfterUnmount = false,
      ...howlConfig
    } = parseHowlConfig(...args);

    if (!interrupt && howlRef.current && howlRef.current.playing()) {
      return howlRef.current;
    }

    unloadAudio();

    const howl = new AquaHowl({
      autoplay: true,
      ...howlConfig,
    });

    if (caption.length) {
      howl.on('play', () => startVoiceCaption(caption));
      howl.on('end', resetVoiceCaption);
    }

    howl.on('play', () => {
      if (howlRef.current && !howlRef.current._willUnmount) {
        setIsAudioPlaying(true);
      }
    });
    howl.on('halt', () => {
      if (howlRef.current && !howlRef.current._willUnmount) {
        setIsAudioPlaying(false);
      }
    });

    howl.volume(audioVolume.current);

    howl._continuePlayingAfterUnmount = continuePlayingAfterUnmount;

    howlRef.current = howl;

    return howl;
  }, [howlRef, setIsAudioPlaying, unloadAudio, resetVoiceCaption, startVoiceCaption]);

  const playAudioPromise = useCallback((...args) => new Promise((resolve) => {
    const howl = playAudio(...args);
    howl.on('end', resolve);
    howl.on('loaderror', resolve);
    howl.on('playerror', resolve);
  }), [playAudio]);

  useWillUnmount(() => {
    if (howlRef.current) {
      howlRef.current._willUnmount = true;
      if (!howlRef.current._continuePlayingAfterUnmount) {
        unloadAudio();
      }
    }
  });

  return {
    playAudio,
    unloadAudio,
    isAudioPlaying,
    howlRef,
    playAudioPromise,
    pauseAudio,
    resumeAudio,
    setVolume,
  };
};

export const withAudioPlayer = (HOC) => {
  const HOCWithHowlPlayer = React.forwardRef((props, ref) => {
    const audioProps = useAudioPlayer();

    return <HOC {...props} {...audioProps} ref={ref} />;
  });

  return HOCWithHowlPlayer;
};

export { AquaHowl as UnloadingHowl }; // TODO: get rid of references to this UnloadingHowl & replace with AquaHowl

export default useAudioPlayer;
